// TanStack Query key factories. `workspaceKeys.all` is the one user-scoped
// resource (the workspace list doesn't change when the current workspace
// changes); every other resource's key is rooted at its natural parent
// scope id (workspaceId for locations/templates, locationId for the screens list,
// screenId for a screen's own sub-resources) so a single resource can be
// invalidated without disturbing its siblings.
//
// Tenant switch (Task 3) evicts everything except the workspace list via
// `tenantPredicate`: any key whose first element isn't the literal
// "workspaces" tag gets removed, regardless of which parent id it's rooted
// at — the eviction is coarse by design, not keyed per-workspace.

import type { Query } from "@tanstack/react-query";

export const statusKeys = {
    list: (workspaceId: string) => [workspaceId, "status"] as const,
};

export const workspaceKeys = {
    all: ["workspaces"] as const,
};

export const locationKeys = {
    list: (workspaceId: string) => [workspaceId, "locations"] as const,
};

export const screenKeys = {
    list: (locationId: string) => [locationId, "screens"] as const,
    detail: (screenId: string) => [screenId, "screen"] as const,
};

export const postingKeys = {
    list: (streamId: string) => [streamId, "postings"] as const,
};

export const templateKeys = {
    list: (workspaceId: string) => [workspaceId, "templates"] as const,
};

export const estimateKeys = {
    list: (screenId: string, from: string, to: string) => [screenId, "estimates", from, to] as const,
};

export const searchKeys = {
    locations: (workspaceId: string, rq: unknown) => ["search", "locations", workspaceId, rq] as const,
    screens: (workspaceId: string, rq: unknown) => ["search", "screens", workspaceId, rq] as const,
    screensAll: (workspaceId: string) => ["search", "screens", workspaceId] as const,
};

export const auditKeys = {
    all: ["audit"] as const,
    search: (workspaceId: string, rq: unknown) => ["audit", "search", workspaceId, rq] as const,
    event: (workspaceId: string, eventId: string) => ["audit", "event", workspaceId, eventId] as const,
};

export function tenantPredicate(query: Pick<Query, "queryKey">): boolean {
    return query.queryKey[0] !== "workspaces";
}
